/*
  Agent Table Component
  ---------------------
  Reusable table for editing agents (industry-specific or general).
*/

import React from 'react';

/**
 * Editable agent table component
 * @param {Object} props
 * @param {Array} props.agents - Array of agent objects
 * @param {number} props.editIdx - Index of agent being edited (-1 if none)
 * @param {Object} props.editObj - Object with edit form values
 * @param {Function} props.setEditObj - Setter for edit form values
 * @param {Object} props.newAgent - Object with new agent form values
 * @param {Function} props.setNewAgent - Setter for new agent form values
 * @param {Function} props.onBeginEdit - Called with index when Edit clicked
 * @param {Function} props.onSaveEdit - Called when Save clicked during edit
 * @param {Function} props.onCancelEdit - Called when Cancel clicked during edit
 * @param {Function} props.onDelete - Called with index when Delete clicked
 * @param {Function} props.onAdd - Called when Add clicked
 * @param {string} props.emptyMessage - Message when no agents
 */
const AgentTable = ({
  agents = [],
  editIdx = -1,
  editObj = {},
  setEditObj,
  newAgent = {},
  setNewAgent,
  onBeginEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onAdd,
  emptyMessage = 'No agents yet.'
}) => {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm border">
        <thead className="bg-gray-100">
          <tr>
            <th className="text-left px-2 py-1">Name</th>
            <th className="text-left px-2 py-1">FTE Saved</th>
            <th className="text-left px-2 py-1">Cost Impact</th>
            <th className="text-left px-2 py-1">Score</th>
            <th className="text-right px-2 py-1">Actions</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((a, idx) => (
            <tr key={idx} className="border-t">
              {editIdx === idx ? (
                <>
                  <td className="px-2 py-1">
                    <input
                      type="text"
                      className="border rounded px-2 py-1 w-full"
                      value={editObj.label || ''}
                      onChange={e => setEditObj(s => ({ ...s, label: e.target.value }))}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="any"
                      className="border rounded px-2 py-1 w-28"
                      value={editObj.timeSavedFTE || ''}
                      onChange={e => setEditObj(s => ({ ...s, timeSavedFTE: e.target.value }))}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="any"
                      className="border rounded px-2 py-1 w-28"
                      value={editObj.costImpactRate || ''}
                      onChange={e => setEditObj(s => ({ ...s, costImpactRate: e.target.value }))}
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      type="number"
                      step="any"
                      className="border rounded px-2 py-1 w-20"
                      value={editObj.score || ''}
                      onChange={e => setEditObj(s => ({ ...s, score: e.target.value }))}
                    />
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded mr-2"
                      onClick={onSaveEdit}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-0.5 bg-gray-200 rounded"
                      onClick={onCancelEdit}
                    >
                      Cancel
                    </button>
                  </td>
                </>
              ) : (
                <>
                  <td className="px-2 py-1 break-all">{a.label}</td>
                  <td className="px-2 py-1">{a.timeSavedFTE}</td>
                  <td className="px-2 py-1">{a.costImpactRate}</td>
                  <td className="px-2 py-1">{a.score ?? ''}</td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      className="text-xs px-2 py-0.5 bg-gray-200 rounded mr-2"
                      onClick={() => onBeginEdit(idx)}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="text-xs px-2 py-0.5 bg-red-600 text-white rounded"
                      onClick={() => onDelete(idx)}
                    >
                      Delete
                    </button>
                  </td>
                </>
              )}
            </tr>
          ))}
          {/* Add new row */}
          <tr className="border-t bg-gray-50">
            <td className="px-2 py-1">
              <input
                type="text"
                placeholder="Name"
                className="border rounded px-2 py-1 w-full"
                value={newAgent.label || ''}
                onChange={e => setNewAgent(s => ({ ...s, label: e.target.value }))}
              />
            </td>
            <td className="px-2 py-1">
              <input
                type="number"
                step="any"
                placeholder="0.05"
                className="border rounded px-2 py-1 w-28"
                value={newAgent.timeSavedFTE || ''}
                onChange={e => setNewAgent(s => ({ ...s, timeSavedFTE: e.target.value }))}
              />
            </td>
            <td className="px-2 py-1">
              <input
                type="number"
                step="any"
                placeholder="0.001"
                className="border rounded px-2 py-1 w-28"
                value={newAgent.costImpactRate || ''}
                onChange={e => setNewAgent(s => ({ ...s, costImpactRate: e.target.value }))}
              />
            </td>
            <td className="px-2 py-1">
              <input
                type="number"
                step="any"
                placeholder="(opt)"
                className="border rounded px-2 py-1 w-20"
                value={newAgent.score || ''}
                onChange={e => setNewAgent(s => ({ ...s, score: e.target.value }))}
              />
            </td>
            <td className="px-2 py-1 text-right">
              <button
                type="button"
                className="text-xs px-2 py-0.5 bg-blue-600 text-white rounded"
                onClick={onAdd}
              >
                Add
              </button>
            </td>
          </tr>
          {agents.length === 0 && (
            <tr>
              <td className="px-2 py-2 text-sm text-gray-500" colSpan={5}>
                {emptyMessage}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default AgentTable;
